import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { memo, useRef, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { COLLECTION_LAYER } from "@shared/types/collection";
import { memoForwardRef } from "@shared/client/util/memo";
import { useApp } from "@/state";
import { useLongPressDrag } from "@/hooks/use-long-press-drag";
import { blockStore } from "@/data/block-store";
import { collectionStore } from "@/data/collection-store";
import { useActiveCollectionId, useRouteEntity, useUrlForCollectionId } from "@/lib/urls";
import { track } from "@/lib/analytics";
import { listCollectionEffectiveMembers } from "@/api/query-api";
import { collectionEffectiveMembersQueryKey } from "@/api/collection-members-query-key";
import { accountAttributionQueryKey } from "@/api/account-attribution-query-key";
import { accountAttributionStore } from "@/data/account-attribution-store";
import { usePrimeAccountAttributionFromMembers } from "@/hooks/use-prime-account-attribution-from-members";
import { useAccountIdentity } from "@/hooks/use-account-identity";
import { userAnnotationStore } from "@/data/user-annotation-store";
import { OPTIMISTIC_OWNER_PLACEHOLDER } from "@/data/user-annotation-optimistic-id";
import { resolveAttributionIdentity } from "./lip-attribution";
import { BlockView, resolveContentComponent, UnsupportedBlockContent, isEmptyBlock, } from "./block-view";
import { AnnotationViewContainer } from "@/components/annotation/annotation-view-container";
import { AnnotationAuthorAvatars } from "@/components/annotation/annotation-author-avatars";
import { InlineAnnotationsLayer } from "@/components/annotation/inline-annotations-layer";
import { BlockPinButton } from "./block-pin-button";
import "./block-view-container.scss";
// ---------------------------------------------------------------------------
// Replier avatar stack
//
// Cap on how many distinct accounts surface as 10×10 chips beneath the
// block creator's avatar. Anything beyond that is silently dropped —
// the corner-meta is a glance-level affordance, not a comprehensive
// participants list (the comments rail / popover own the full set).
// Picked tight so a thread with many participants doesn't grow the
// stack past the card's vertical footprint.
// ---------------------------------------------------------------------------
const MAX_REPLIER_AVATARS = 5;
// ---------------------------------------------------------------------------
// Stale-processing spinner suppression
//
// A block that's been `pending`/`processing` for this long is almost
// certainly stuck — the server-side hydration poll runs every 10s and the
// terminal-error timer in `block-store.ts` flips it to `errored` at 5min,
// but in between the spinner reads as "actively loading" even though the
// block is sitting idle with its placeholder content. After this window
// elapses we drop just the spinner overlay; the card keeps its
// pending-state semantics (no pin button, no annotation layer, lower
// affordance) so we don't accidentally promote a half-loaded card to
// "fully interactive" — we only stop lying about active work.
// ---------------------------------------------------------------------------
const STALE_PROCESSING_MS = 30_000;
const SELECTION_OVERLAY = (_jsx("div", { className: "block-view__select-overlay", "aria-hidden": true, children: _jsx("div", { className: "block-view__select-checkbox" }) }));
/**
 * Single 10×10 avatar chip rendered inside `.block-view__avatar-stack`.
 * Owns its own `useAccountIdentity` subscription so each chip
 * re-renders independently when its account's attribution lands —
 * adding a new commenter doesn't churn unrelated chips on the same
 * card.
 */
const ReplierAvatar = memo(function ReplierAvatar({ accountId }) {
    const identity = useAccountIdentity(accountId);
    return (_jsx("span", { className: "block-view__replier-avatar", "aria-hidden": true, children: identity.avatarSrc ? (_jsx("img", { src: identity.avatarSrc, alt: "", className: "block-view__replier-avatar-image" })) : (_jsx("span", { className: "block-view__replier-avatar-initial", children: identity.initial })) }));
});
const EMPTY_COLLECTION_EFFECTIVE_MEMBERS_QUERY_KEY = [
    "collection.effectiveMembers",
    "none",
];
/**
 * Drop focus from any active text input before a block expands. iOS Safari
 * keeps the on-screen keyboard up as long as the omnibar's textarea (or any
 * other text input) holds focus — even after the block detail modal slides
 * in over it — so the user is left with the keyboard hovering above an
 * unrelated, unedited surface. Calling `.blur()` synchronously on the
 * gesture release dismisses the keyboard cleanly, in lockstep with the
 * modal's open animation.
 *
 * Desktop browsers don't auto-blur on clicks landing on non-focusable
 * elements either, so the same call also releases the omnibar visually
 * (focus ring, expanded width) on click-to-expand. That matches the
 * intent: the user has stopped editing the moment they reach for a block.
 *
 * Scoped to text-entry surfaces only — buttons, the modal's toolbar
 * controls, etc. retain focus so keyboard navigation still works inside
 * the modal once it opens.
 */
function dismissActiveTextFocus() {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement))
        return;
    const isTextInput = active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable;
    if (isTextInput)
        active.blur();
}
/**
 * Collect block ids visible in the same feed as `originEl`, in DOM
 * (visual reading) order. Used by shift-click range selection: the
 * range is measured against this list so the user sees "click A,
 * shift-click B → everything I see between them gets selected",
 * regardless of how the underlying data is grouped or sorted.
 *
 * Scoped to the nearest `.stream` ancestor — every surface that
 * renders blocks does so through `<Stream>` (collection detail,
 * smart collection, archive, group view), so the closest stream
 * root scopes the walk to "what's visible in THIS feed" and never
 * crosses into sibling feeds (block detail modal sidebar, sample
 * showcases). Falls back to document scope only when the click
 * somehow originates outside a stream — a defensive path; under
 * normal use a card always lives inside one.
 */
function getOrderedBlockIdsInSurface(originEl) {
    if (!originEl)
        return [];
    const container = originEl.closest(".stream") ?? document.body;
    const nodes = container.querySelectorAll("[data-block-id]");
    const ids = [];
    const seen = new Set();
    for (const node of nodes) {
        const id = node.getAttribute("data-block-id");
        if (id !== null && id !== "" && seen.has(id) === false) {
            seen.add(id);
            ids.push(id);
        }
    }
    return ids;
}
export const BlockViewContainer = memoForwardRef(({ blockId, dateChipMode, inUnfurledStack = false }, ref) => {
    const app = useApp();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const buildUrlForCollectionId = useUrlForCollectionId();
    // Each card subscribes to its own envelope. Updates to one block's
    // envelope (placeholder swap on capture, surfaceAt change on bump,
    // soft-delete) trigger a re-render of just that card, never the
    // parent feed. `useById` falls through to a network fetch on cache
    // miss so cards reached via routes that didn't seed the cache
    // (e.g. a modal opened from a direct URL) still render correctly.
    //
    // The envelope can be `undefined` for one render between mount and
    // the cache resolving (or longer if a network fallback is needed),
    // so every hook below has to accept "no envelope yet" without
    // throwing. The actual skeleton swap happens at the bottom of the
    // component, after every hook has been called — Rules of Hooks
    // forbid conditional returns above this line.
    const envelope = blockStore.useById(blockId);
    const block = envelope?.block;
    const cardId = blockId;
    const ContentComponent = block
        ? resolveContentComponent(block) ?? UnsupportedBlockContent
        : UnsupportedBlockContent;
    const empty = block ? isEmptyBlock(block) : true;
    // -- Refs ----------------------------------------------------------------
    const rootRef = useRef(null);
    const setRootNode = useCallback((node) => {
        rootRef.current = node;
        if (typeof ref === "function")
            ref(node);
        else if (ref)
            ref.current = node;
    }, [ref]);
    // -- Store subscriptions --------------------------------------------------
    const cardState = app.useStore((s) => s.cards.get(cardId));
    const isSelected = app.useStore((s) => s.selection.has(cardId));
    const isLassoHovered = app.useStore((s) => s.lassoSelection.has(cardId));
    const isActive = app.useStore((s) => s.ui.activeBlockId === cardId);
    // `isMultiselectMode` is intentionally NOT subscribed here. The flag
    // flipping on long-press would otherwise re-render every visible
    // block in the feed (N tiles all flip in the same commit). All
    // multiselect-mode appearance now cascades from the
    // `.app-screen--multiselect` ancestor class (set by
    // `AppScreenRoot` in `app-screen.tsx`), so:
    //   - the selection overlay is always rendered (CSS hides it
    //     outside multiselect via `display: none`); the inner check
    //     icon is gated on `isSelected`, which is per-block.
    //   - the pin button stays mounted; CSS hides it during
    //     multiselect via the ancestor class.
    //   - cursor / press-scale / postit-notch overrides are pure CSS.
    // Event handlers below still need to branch on multiselect state,
    // but they read `app.state.ui.isMultiselectMode` synchronously
    // (no subscription, no per-flag re-render).
    const zIndex = cardState?.zIndex;
    const routeEntity = useRouteEntity();
    const openSource = useMemo(() => {
        switch (routeEntity.type) {
            case "blockDeeplink":
                return "deeplink";
            case "entity":
            case "transientCollection":
            case "standaloneCollection":
            case "collectionBySlug":
                return "collection";
            case "name":
                return "profile";
            case "home":
            case "collectionsTab":
            case "unknown":
            default:
                return "stream";
        }
    }, [routeEntity.type]);
    const blockTypeForEvent = block?.type ?? "unknown";
    // -- Block params (viewer prefs forwarded to block views) -----------------
    const useTwitterBird = app.useStore((s) => s.preferences["eggs.useTwitterBird"] ?? false);
    const onToggleTwitterLogo = useCallback(() => {
        app.setPreferences({ "eggs.useTwitterBird": !useTwitterBird });
    }, [app, useTwitterBird]);
    // Stream cards deliberately don't wire `onToggleTask` — a tap on a
    // task row in a small card surface should expand the card (so the
    // user sees the full list and the rest of the card chrome) rather
    // than flipping a checkbox in place. `TextBlockView` only renders
    // task checkboxes as interactive when `blockParams.isExpanded` is
    // true, which only the modal sets (see
    // `BlockDetailModalContainer.blockParams`). With no callback wired,
    // the checkboxes render as `disabled` + `pointer-events: none`
    // (see the `&[disabled]` rule in `view.scss`), so the tap bubbles
    // up to this container's `onClick` and opens the detail modal.
    const blockParams = useMemo(() => ({ useTwitterBird, onToggleTwitterLogo }), [useTwitterBird, onToggleTwitterLogo]);
    // -- Drag gesture ---------------------------------------------------------
    const onTap = useCallback(() => {
        if (app.state.ui.isMultiselectMode) {
            app.toggleSelection(cardId);
            return;
        }
        dismissActiveTextFocus();
        const el = rootRef.current;
        if (el) {
            const rect = el.getBoundingClientRect();
            el.dispatchEvent(new CustomEvent("block-expand", {
                bubbles: true,
                detail: {
                    blockId: cardId,
                    rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
                },
            }));
        }
        track({
            name: "block.opened",
            props: { source: openSource, block_type: blockTypeForEvent },
        });
        app.setActiveBlock(cardId);
    }, [app, cardId, openSource, blockTypeForEvent]);
    // Long-press primitive: enters block multi-select. dnd-kit's
    // `<DndContext>` (mounted at the Stream level) owns drag — its
    // `MouseSensor` / `TouchSensor` activate on the first pointer
    // movement after mousedown, so a click with no movement still
    // reaches `onClick` below and a long stationary press still
    // reaches the long-press timer above. The two pipelines coexist
    // because they trigger on disjoint pointer-event shapes.
    useLongPressDrag(cardId, rootRef, { onTap });
    const onClick = useCallback((e) => {
        e.stopPropagation();
        // Shift-click extends the selection from the anchor (the
        // most-recently-clicked block) through this card in DOM
        // order — Finder-style range select. `selectRange` is
        // self-seeding: if there's no anchor yet (first shift-click,
        // or the anchor's been deselected/unmounted), it enters
        // multiselect mode with just this card, which is the same
        // outcome the old cmd/shift path produced. A follow-up
        // shift-click then has an anchor to measure from.
        if (e.shiftKey) {
            e.preventDefault();
            const orderedIds = getOrderedBlockIdsInSurface(rootRef.current);
            app.selectRange(cardId, orderedIds);
            return;
        }
        // Cmd-click keeps the discrete toggle semantics: add/remove
        // individual blocks without touching the rest of the
        // selection. Also enters multiselect mode if we're not in
        // it yet — same affordance as the long-press primitive.
        if (e.metaKey) {
            e.preventDefault();
            if (app.state.ui.isMultiselectMode === false) {
                app.enterMultiselectMode(cardId);
                return;
            }
            app.toggleSelection(cardId);
            return;
        }
        if (app.state.ui.isMultiselectMode) {
            e.preventDefault();
            app.toggleSelection(cardId);
            return;
        }
        dismissActiveTextFocus();
        const el = rootRef.current;
        if (el) {
            const rect = el.getBoundingClientRect();
            el.dispatchEvent(new CustomEvent("block-expand", {
                bubbles: true,
                detail: {
                    blockId: cardId,
                    rect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
                },
            }));
        }
        track({
            name: "block.opened",
            props: { source: openSource, block_type: blockTypeForEvent },
        });
        app.setActiveBlock(cardId);
    }, [app, cardId, openSource, blockTypeForEvent]);
    // -- Parent collection lip ------------------------------------------------
    //
    // When a block lives in a non-primary collection AND we're not currently
    // browsing inside that collection's detail view, peek the collection's
    // name as a "lip" below the card. The lip is a host-level decoration
    // (sibling of `.block-view`) so it doesn't get clipped by the block
    // view's `overflow: hidden`, and so the host can shrink the inner card
    // by exactly the lip's height — keeping the overall card slot the same
    // height as cards without a lip.
    const activeCollectionId = useActiveCollectionId();
    // Cross-layer lookup so the lip resolves regardless of the container
    // collection's place in the hierarchy. `useList()` filters out
    // Group-rooted Collections (they live inside the Group's view, not
    // the cross-cutting Collections tab) — using it here meant blocks
    // sitting inside a Group-rooted Collection silently dropped their
    // lip. `useFindById()` reads the unfiltered cross-layer cache so
    // every non-Primary container surfaces correctly.
    const containerCollection = collectionStore.useFindById(block?.collectionId);
    // If the block's container is a Stack, look up the Stack's parent
    // so the lip can dereference to it. The parent can be either a
    // layer-1 Collection or a layer-0 Group (under the relaxed model;
    // see `LEGAL_PARENT_CHILD_LAYER_PAIRS`). The lip represents the
    // block's containing-surface membership — Stacks are sub-
    // organization within a Collection OR a Group, so a block sitting
    // in a Stack still belongs to the parent surface from the lip's
    // perspective. Adding a block to a Stack should never create a
    // new lip OR strip the existing lip; surfacing the parent here
    // preserves the lip that the block earned when it first joined.
    //
    // `useFindById(undefined)` returns undefined, so this is a no-op
    // cost when the container isn't a Stack.
    const parentOfStackContainer = collectionStore.useFindById(containerCollection?.layerIndex === COLLECTION_LAYER.stack
        ? containerCollection.parentCollectionId
        : undefined);
    const parentCollection = useMemo(() => {
        if (!block?.collectionId)
            return undefined;
        if (activeCollectionId && activeCollectionId === block.collectionId) {
            return undefined;
        }
        if (!containerCollection)
            return undefined;
        if (containerCollection.type === "primary")
            return undefined;
        // Dereference Stack containers to the parent (Collection OR
        // Group). The lip surfaces containing-surface membership ("this
        // block is in Collection/Group X"), which a Stack add should
        // NOT remove. Stacks also aren't a navigation target — clicking
        // a "Go to Stack 1" lip would route to a non-tab id; pointing
        // at the parent sends the user to a real destination instead.
        // Primary parents are suppressed (Stacks under Primary are
        // private "stash" Stacks created from the homepage Stream — no
        // public lip to surface).
        let lipCollection = containerCollection;
        if (containerCollection.layerIndex === COLLECTION_LAYER.stack) {
            if (!parentOfStackContainer)
                return undefined;
            if (parentOfStackContainer.type === "primary")
                return undefined;
            lipCollection = parentOfStackContainer;
        }
        // After dereference, suppress the lip when the user is already
        // browsing the resolved Collection (mirrors the existing
        // activeCollectionId === block.collectionId guard above, but on
        // the post-dereference id).
        if (activeCollectionId && activeCollectionId === lipCollection.id) {
            return undefined;
        }
        return lipCollection;
    }, [activeCollectionId, block?.collectionId, containerCollection, parentOfStackContainer]);
    // Suppress the lip inside an unfurled stack: the open stack tile +
    // StackFrame already surface the parent-collection context, so a
    // per-card lip would just restate "in <collection>" beneath every
    // unfurled card. See `inUnfurledStack` in the props doc above.
    const showLip = parentCollection !== undefined && inUnfurledStack === false;
    const lipTitle = parentCollection?.title?.trim() || "Untitled collection";
    const collectionId = parentCollection?.id;
    // The effective-members fetch + attribution priming runs whenever
    // ANY of the card's chrome needs avatar attribution to resolve:
    //
    //   - `showLip` (the lip's own avatar — the original case)
    //   - `inUnfurledStack && parentCollection !== undefined` — the new
    //     creator-avatar-in-unfurl case. The homepage Stream doesn't
    //     prime per-collection attribution itself (that happens at the
    //     `CollectionDetailTabContent` level), so without this branch
    //     the in-unfurl creator avatar would fall through to a single-
    //     letter placeholder until something else loaded the members.
    //
    // The same `parentCollection.id` keys both: it's the dereferenced
    // layer-1 container the lip would have pointed at, which is also
    // the surface whose member list determines who can have authored
    // the block.
    const needsAttributionPriming = collectionId !== undefined &&
        (showLip || (inUnfurledStack && parentCollection !== undefined));
    const membersQueryKey = collectionId
        ? collectionEffectiveMembersQueryKey(collectionId)
        : EMPTY_COLLECTION_EFFECTIVE_MEMBERS_QUERY_KEY;
    const membersQuery = useQuery({
        queryKey: membersQueryKey,
        queryFn: () => listCollectionEffectiveMembers(collectionId ?? ""),
        enabled: needsAttributionPriming,
        staleTime: 15_000,
    });
    usePrimeAccountAttributionFromMembers(membersQuery.data, queryClient, collectionId, membersQuery.dataUpdatedAt, needsAttributionPriming);
    const attributionQuery = useQuery({
        queryKey: [
            ...accountAttributionQueryKey(block?.createdBy ?? ""),
            collectionId ?? "",
            membersQuery.dataUpdatedAt,
        ],
        queryFn: () => {
            if (!block)
                return resolveAttributionIdentity("", undefined);
            const members = membersQuery.data;
            if (members !== undefined && members.length > 0) {
                return resolveAttributionIdentity(block.createdBy, members);
            }
            const fromStore = accountAttributionStore.getState().byAccountId[block.createdBy];
            if (fromStore !== undefined) {
                return fromStore;
            }
            return resolveAttributionIdentity(block.createdBy, undefined);
        },
        enabled: showLip && collectionId !== undefined && block !== undefined,
        staleTime: 60_000,
    });
    const addedByIdentity = attributionQuery.data ??
        resolveAttributionIdentity(block?.createdBy ?? "", membersQuery.data);
    const onLipClick = useCallback((e) => {
        e.stopPropagation();
        // Guard against stale callbacks while collection context is resolving.
        if (!collectionId)
            return;
        track({ name: "collection.opened", props: { source: "lip" } });
        navigate(buildUrlForCollectionId(collectionId));
    }, [navigate, collectionId, buildUrlForCollectionId]);
    // -- Creator avatar (in shared active collection or unfurled stack) ------
    //
    // When the user is browsing inside a shared (non-Primary-rooted) layer-1
    // Collection — or has unfurled a stack whose container collection is
    // shared (the open synthetic grouped-by-collection tile on the
    // homepage, or a real Stack inside a shared Collection) — the lip is
    // suppressed (the surface already announces the parent collection),
    // so there's no "who put this here" affordance on the card. Surface
    // that attribution as a small profile-picture overlay in the card's
    // bottom-right corner — one per block, resolved via the shared
    // `useAccountIdentity` pipeline that's already primed from the
    // active collection's effective-members query (see
    // `CollectionDetailTabContent` in `app-screen.tsx`).
    //
    // Detection rules (mirroring `collection-settings-modal.tsx`):
    //   - `activeCollection` resolved via the cross-layer cache
    //   - layer-1 only (Stacks not handled in this iteration; their
    //     attribution would need to walk up to the layer-1 ancestor)
    //   - parent's `type !== "primary"` covers BOTH standalone (no
    //     parent) AND Group-rooted, while excluding Primary-rooted
    //     (private to this user).
    const activeCollection = collectionStore.useFindById(activeCollectionId);
    const activeCollectionParent = collectionStore.useFindById(activeCollection?.parentCollectionId);
    const isInSharedActiveCollection = activeCollection !== undefined &&
        activeCollection.layerIndex === COLLECTION_LAYER.collection &&
        activeCollectionParent?.type !== "primary";
    // For the unfurled-stack case the relevant "context collection"
    // isn't the URL-derived active collection — it's the block's own
    // container (or, if that container is a Stack, the Stack's parent).
    // Apply the same layer-1 + non-Primary-rooted gate to that
    // dereferenced container so the avatar only renders when the
    // unfurled surface is actually multi-account.
    const effectiveContainerCollection = containerCollection?.layerIndex === COLLECTION_LAYER.stack
        ? parentOfStackContainer
        : containerCollection;
    const effectiveContainerParent = collectionStore.useFindById(effectiveContainerCollection?.parentCollectionId);
    const isEffectiveContainerShared = effectiveContainerCollection !== undefined &&
        effectiveContainerCollection.layerIndex === COLLECTION_LAYER.collection &&
        effectiveContainerParent?.type !== "primary";
    // Show the avatar when EITHER:
    //   1. The user is browsing inside a shared layer-1 collection AND
    //      the block lives in that collection (the original case — the
    //      `=== block.collectionId` guard catches the pathological case
    //      where a card rendered in a collection-detail surface points
    //      at a block from a different collection), OR
    //   2. The card is rendered inside an open unfurl whose effective
    //      container is a shared layer-1 collection (the new case — see
    //      `inUnfurledStack` prop doc).
    const showCreatorAvatar = block !== undefined &&
        ((isInSharedActiveCollection && activeCollectionId === block.collectionId) ||
            (inUnfurledStack && isEffectiveContainerShared));
    // `useAccountIdentity` reads from the Zustand store primed by
    // `usePrimeAccountAttributionFromMembers` — the prime side-effect
    // runs once per shared collection in `CollectionDetailTabContent`,
    // so by the time the cards mount the entry is typically already
    // present. Falls back to a placeholder initial when the store
    // hasn't seen the account yet.
    const creatorIdentity = useAccountIdentity(block?.createdBy);
    const creatorAvatar = showCreatorAvatar ? (_jsx("span", { className: "block-view__creator-avatar", "aria-hidden": true, children: creatorIdentity.avatarSrc ? (_jsx("img", { src: creatorIdentity.avatarSrc, alt: "", className: "block-view__creator-avatar-image" })) : (_jsx("span", { className: "block-view__creator-avatar-initial", children: creatorIdentity.initial })) })) : null;
    // -- Replier avatars (in shared active collection) ------------------------
    //
    // Stack 10×10 avatar chips for every distinct account (other than
    // the block's creator) that has annotated the block — comments,
    // reactions (stickers/stamps), highlights, drawings — beneath the
    // creator's avatar. Surfaces "who else is in the conversation" at
    // a glance regardless of HOW they engaged (a sticker reaction is
    // as much a "reply" in spirit as a typed comment); the corner-meta
    // is the social-presence affordance for a shared collection card,
    // complementing the comments rail/popover that own the actual
    // thread reading surface.
    //
    // Gated on the same `showCreatorAvatar` predicate as the creator
    // avatar itself: in a private (Primary-rooted) collection there's
    // nobody but the user, so the stack would either be empty or
    // confusing — and there's no creator avatar above it to anchor to.
    //
    // Annotations come from the local TanStack DB cache primed by the
    // stream's `useAnnotationsForBlocks` warming hook, so the chips
    // appear without any per-card network call. Optimistic-owner
    // placeholders are filtered out so a row mid-create doesn't render
    // as a `?` chip. Capped at `MAX_REPLIER_AVATARS` so a busy thread
    // doesn't grow the stack past the card's vertical footprint.
    const { data: blockAnnotations } = userAnnotationStore.useByBlock(showCreatorAvatar ? blockId : undefined);
    const replierAccountIds = useMemo(() => {
        if (!showCreatorAvatar)
            return [];
        const creatorId = block?.createdBy;
        const seen = new Set();
        const out = [];
        for (const annotation of blockAnnotations) {
            const ownerId = annotation.ownerId;
            if (!ownerId)
                continue;
            if (ownerId === OPTIMISTIC_OWNER_PLACEHOLDER)
                continue;
            if (creatorId !== undefined && ownerId === creatorId)
                continue;
            if (seen.has(ownerId))
                continue;
            seen.add(ownerId);
            out.push(ownerId);
            if (out.length >= MAX_REPLIER_AVATARS)
                break;
        }
        return out;
    }, [blockAnnotations, block?.createdBy, showCreatorAvatar]);
    const replierAvatars = replierAccountIds.length > 0 ? (_jsx(_Fragment, { children: replierAccountIds.map((id) => (_jsx(ReplierAvatar, { accountId: id }, id))) })) : null;
    // -- Pending state --------------------------------------------------------
    //
    // Treat a block as "pending" whenever:
    //   - the server is still processing it, OR
    //   - it has no displayable content yet (e.g. a link envelope whose URL
    //     hasn't been filled in by the ingestor). The latter case used to
    //     render the dashed ERROR shell, which read as a hard failure even
    //     though the row was simply still preloading. Showing the same
    //     spinner shell as the explicit "processing" state keeps the
    //     affordance honest.
    // Until the envelope arrives, treat the card as pending unconditionally
    // so the lower render path produces the same skeleton it does for a
    // server-side processing block. Once the envelope lands, the normal
    // status check takes over.
    const isProcessing = block?.processingStatus === "pending" || block?.processingStatus === "processing";
    const isPending = !block || isProcessing || empty;
    // Suppress the spinner overlay once a pending block has been sitting
    // around past `STALE_PROCESSING_MS`. We tick `nowMs` once when the
    // block first crosses the threshold so a card that was created at
    // t=0 and is still pending at t=30s flips on its own — without the
    // timer the spinner would linger until some unrelated re-render
    // (parent list refetch, hover, etc.) happened to repaint the card.
    // Cleared on unmount and re-armed whenever a fresh envelope lands.
    const [nowMs, setNowMs] = useState(() => Date.now());
    const createdAtMs = block ? new Date(block.createdAt).getTime() : undefined;
    useEffect(() => {
        if (createdAtMs === undefined || isProcessing === false)
            return;
        const remaining = createdAtMs + STALE_PROCESSING_MS - Date.now();
        if (remaining <= 0) {
            setNowMs(Date.now());
            return;
        }
        const handle = setTimeout(() => setNowMs(Date.now()), remaining);
        return () => clearTimeout(handle);
    }, [createdAtMs, isProcessing]);
    const isStaleProcessing = createdAtMs !== undefined &&
        isProcessing &&
        nowMs - createdAtMs >= STALE_PROCESSING_MS;
    // Text blocks render their full displayable content immediately
    // from the optimistic placeholder — server-side processing
    // (analysis, embeddings, categorisation) doesn't change anything
    // the user sees inside the card. The spinner badge in that case
    // is pure noise on top of an already-finished-looking card, so
    // suppress it for non-empty text blocks. Empty text blocks (e.g.
    // a cache-miss stub with `text: ""`) still get the spinner because
    // there's nothing else on the card to read as "in flight".
    const suppressPendingOverlay = block?.type === "text" && empty === false;
    const hostClassName = [
        "block-view-host",
        isActive && "block-view-host--active",
        showLip && "block-view-host--with-lip",
    ]
        .filter(Boolean)
        .join(" ");
    // Envelope hasn't landed yet (cache miss + network fetch in flight).
    // Render a dedicated skeleton sized to the regular card slot so the
    // feed's layout doesn't shift when the real envelope upserts. All
    // hooks above run unconditionally — early-return is safe here.
    if (!envelope || !block) {
        return (_jsx("div", { ref: setRootNode, className: "block-view-container block-view-container--pending", "data-block-id": blockId, children: _jsx("div", { className: "block-view__pending-overlay", children: _jsx("div", { className: "block-view__pending-spinner" }) }) }));
    }
    const card = (_jsx(BlockView, { ref: setRootNode, envelope: envelope, ContentComponent: ContentComponent, blockParams: blockParams, isSelected: isSelected, isLassoHovered: isLassoHovered, zIndex: zIndex, toolbar: SELECTION_OVERLAY, onClick: onClick, treatEmptyAsPending: isPending, dateChipMode: dateChipMode, creatorAvatar: creatorAvatar, replierAvatars: replierAvatars }));
    // Annotations layer: hosts BOTH the inline blend layer
    // (text_highlight + text-range comment rects, blended with the
    // card text) AND the positioning container (stickers / drawings
    // / comment pins / attribution avatars). Mounted as a sibling of
    // `.block-view` (not inside it) so the card's `overflow: hidden`
    // doesn't clip pin overshoot. The wrapper class re-applies the
    // lipped-height shrink so the visible annotation footprint
    // exactly matches the visible card area — text rects don't
    // paint over the lip strip even when the underlying text reflows
    // past the visible card height.
    //
    // Source order inside the wrapper:
    //   1. `<InlineAnnotationsLayer>` first so the positioning
    //      container paints OVER the highlights (a pin sitting on
    //      top of a highlight reads right-side up).
    //   2. `<AnnotationViewContainer>` last so its stamps / drawings
    //      / pins sit above both the card text AND the highlights.
    //
    // The wrapper itself doesn't form a stacking context (no
    // `z-index`, `isolation`, `opacity < 1`, `filter`, `transform`,
    // `mask`) — that's what lets the inline layer's
    // `mix-blend-mode: multiply` rects composite against the
    // `.block-view` content painted earlier in the host's stacking
    // context. The positioning container DOES form a stacking
    // context (its own `transform: scale(...)`), but it's a
    // SIBLING of the inline layer, so its isolation only affects
    // its own children — the inline layer's rects are unaffected.
    //
    // `mode="regular"` on the positioning container disables
    // gestures (the card itself is a drag target; the small card is
    // too small to wrangle resize/rotate handles anyway). Hidden
    // whenever this card is the active modal target — the modal
    // owns rendering during the FLIP grow/shrink, and a duplicate
    // annotations layer here would double-paint over the dashed
    // placeholder.
    const annotationsLayer = isActive || isPending ? null : (_jsxs("div", { className: [
            "block-view-host__annotations",
            showLip && "block-view-host__annotations--with-lip",
        ]
            .filter(Boolean)
            .join(" "), children: [_jsx(InlineAnnotationsLayer, { blockId: cardId, mode: "regular" }), _jsx(AnnotationViewContainer, { blockId: cardId, mode: "regular" }), showCreatorAvatar === false ? (_jsx(AnnotationAuthorAvatars, { blockId: cardId })) : null] }));
    // Per-card pin button. Surfaces on hover whenever the block sits
    // in a non-Primary parent — that's "in a Stack" or "in a non-
    // Primary Collection". Toggles the block's pin in that parent's
    // default surface directly (no popover — exactly one destination).
    // Hidden while the modal owns the card (`isActive`) or while still
    // pending. Multiselect-mode hiding is handled purely in CSS via
    // `.app-screen--multiselect` (see `block-view-container.scss`) so
    // entering multiselect doesn't re-render every visible block just
    // to drop one absolutely-positioned affordance.
    //
    // `containerCollection` is the block's IMMEDIATE parent (Stack OR
    // layer-1 Collection). The block-view lip surfaces the layer-1
    // ancestor for Stack-housed blocks, but the pin slot stays on the
    // immediate parent — pinning a block inside an open Stack should
    // pin it to the top of THAT Stack's unfurl, not bubble up to the
    // ancestor Collection.
    const canShowPinButton = containerCollection !== undefined &&
        containerCollection.type !== "primary" &&
        isActive === false &&
        isPending === false;
    const pinButton = canShowPinButton ? (_jsx(BlockPinButton, { blockId: cardId, parent: {
            id: containerCollection.id,
            title: containerCollection.title?.trim() || "Untitled",
        } })) : null;
    const lip = showLip ? (_jsxs("button", { type: "button", className: "block-view-host__lip", onClick: onLipClick, "aria-label": `Go to ${lipTitle}`, children: [_jsx("span", { className: "block-view-host__lip-label", children: "\u2937" }), _jsx("span", { className: "block-view-host__lip-title", children: lipTitle }), _jsx("span", { className: "block-view-host__lip-attribution", children: _jsx("span", { className: "block-view-host__lip-avatar", children: addedByIdentity.avatarSrc ? (_jsx("img", { src: addedByIdentity.avatarSrc, alt: "", className: "block-view-host__lip-avatar-image" })) : (addedByIdentity.initial) }) })] })) : null;
    if (isPending) {
        return (_jsxs("div", { className: suppressPendingOverlay ? hostClassName : `${hostClassName} block-view--pending`, children: [lip, card, isStaleProcessing || suppressPendingOverlay ? null : (_jsx("div", { className: "block-view__pending-overlay", children: _jsx("div", { className: "block-view__pending-spinner" }) }))] }));
    }
    // Source-order stacking: lip first, then card, then the
    // annotations layer (which itself nests the inline blend layer
    // before the positioning container). The host IS its own
    // stacking context (via `container-type: size`), but its
    // children with `z-index: auto` paint in tree order against a
    // shared backdrop:
    //
    //   - Lip first so the card's silhouette occludes its overlap.
    //   - Card next so its text is the blend backdrop for the
    //     inline layer that follows.
    //   - Annotations wrapper last so drawings / marks / pins paint
    //     over the card text AND over the highlights.
    //
    // The wrapper itself doesn't carry `z-index` — it relies on
    // this DOM order, which is what keeps `mix-blend-mode: multiply`
    // working in the inline layer it nests (an explicit `z-index`
    // would force a stacking context and auto-isolate the blend).
    // See the matching comments in `block-view-container.scss` and
    // `annotation.scss`.
    return (_jsxs("div", { className: hostClassName, children: [lip, card, pinButton, annotationsLayer] }));
});
